import { useEffect, useMemo, useState } from 'react';
import Odometer from '~/components/stats/Odometer';
import clickIcon from '~/images/click_icon.svg';
import uniqueVisitsIcon from '~/images/unique_visitors.svg';
import viewsIcon from '~/images/view_icon.svg';
import { formatNumber } from '~/utils/formatting';
import { summarizeEvents } from '~/utils/stats';
import type { BlinkLiveData } from '~/types';

interface BlinkStatsProps {
    blinkClicksLastMonth: Promise<number>;
    uniqueUsersBlink: Promise<number>;
    uniqueUsersNonBlink: Promise<number>;
    aggregatedData: BlinkLiveData;
}

export function BlinkStats({ blinkClicksLastMonth, uniqueUsersBlink, uniqueUsersNonBlink, aggregatedData }: BlinkStatsProps) {
    const clickedEventsCount = useMemo(() => summarizeEvents(aggregatedData, 'Clicked'), [aggregatedData]);
    const viewedEventsCount = useMemo(() => summarizeEvents(aggregatedData, 'Viewed'), [aggregatedData]);
    const [isLoading, setIsLoading] = useState(true);
    const [uniqueUsersData, setUniqueUsersData] = useState<[number, number]>([0, 0]);
    const [totalClicks, setTotalClicks] = useState<number>(0);

    useEffect(() => {
        Promise.all([uniqueUsersBlink, uniqueUsersNonBlink])
            .then(setUniqueUsersData)
            .finally(() => setIsLoading(false));
    }, [uniqueUsersBlink, uniqueUsersNonBlink]);

    useEffect(() => {
        blinkClicksLastMonth.then((blinkClicks) => {
            setTotalClicks(blinkClicks + clickedEventsCount);
        });
    }, [blinkClicksLastMonth, clickedEventsCount]);

    return (
        <div className="text-blue-alt-900 my-32 flex flex-col gap-16 md:my-64 md:gap-24">
            <div className="w-full rounded-16 bg-green-100">
                <div className="mx-44 my-24 flex flex-col text-left md:my-44">
                    <img className="mb-24" height={45} width={36} src={clickIcon} alt="click icon" />
                    <div className="flex flex-row items-end">
                        <Odometer count={totalClicks} className="text-blue-alt-900" />
                        <p className="nomobile ml-16 text-34">klikk</p>
                    </div>
                    <p className="nomobile text-16">
                        på <strong>boligannonser</strong> med Blink den siste måneden
                    </p>
                    <p className="onlymobile-block text-12">
                        klikk på <b>boligannonser</b> med Blink den siste måneden
                    </p>
                </div>
            </div>
            <div className="grid grid-cols-1 gap-16 md:grid-cols-2">
                <div className="w-full rounded-16 bg-green-100">
                    <div className="mx-44 my-24 flex flex-col text-left md:my-44">
                        <img className="mb-24" height={52} width={60} src={uniqueVisitsIcon} alt="unique visitors icon" />
                        <div className="flex flex-row items-end">
                            {isLoading ? <span className="odometer-number">0</span> : uniqueUsers(uniqueUsersData[0], uniqueUsersData[1])}
                            <p className="mb-0 ml-4 text-34 md:mb-8 md:ml-16">%</p>
                        </div>
                        <p className="text-12 md:text-16">
                            flere <b>unike besøkende</b> på boligannonser med Blink
                        </p>
                    </div>
                </div>
                <div className="w-full rounded-16 bg-green-100">
                    <div className="mx-44 my-24 flex flex-col text-left md:my-44">
                        <img className="mb-24" height={48} width={60} src={viewsIcon} alt="views icon" />
                        <div className="flex flex-row items-end">
                            <Odometer count={viewedEventsCount} className="text-blue-alt-900" />
                            <p className="nomobile ml-16 text-34">visninger</p>
                        </div>
                        <p className="nomobile text-16">
                            av <b>Blink-kampanjer</b> siden du åpnet denne siden
                        </p>
                        <p className="onlymobile-block text-12">
                            visninger av <b>Blink-kampanjer</b> siden du åpnet denne siden
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
}

const uniqueUsers = (uniqueUsersBlink: number, uniqueUsersNonBlink: number) => {
    return (
        <span className="odometer-number">
            <div
                style={{
                    height: '1em',
                    lineHeight: '0.95em',
                    alignItems: 'flex-end',
                }}>
                <p>{formatNumber(uniqueUsersNonBlink > 0 ? (100 * (uniqueUsersBlink - uniqueUsersNonBlink)) / uniqueUsersNonBlink : 0)}</p>
            </div>
        </span>
    );
};
